import { ReactSelectOptions } from "../types";
import mapKnownValues from "../utils/mapKnownValues";
import AggregatorLink, { AggregatorLinkResponse } from "./aggregatorLink";

export const SalaryRangeValues = {
  "Below $30,000": "Below $30,000",
  "$30,000 to $50,000": "$30,000 to $50,000",
  "$50,000 to $70,000": "$50,000 to $70,000",
  "$70,000 to $90,000": "$70,000 to $90,000",
  "$90,000 to $110,000": "$90,000 to $110,000",
  "Above $110,000": "Above $110,000",
};

export type SalaryRange = keyof typeof SalaryRangeValues;

export const SalaryRangeValueOptions: ReactSelectOptions = Object.keys(SalaryRangeValues).map((key) => {
  return {
    value: key,
    label: SalaryRangeValues[key],
  };
});

export const UNKNOWN = "UNKNOWN";
export const TRUE = "TRUE";
export const FALSE = "FALSE";
export type SkippableBoolean = typeof UNKNOWN | typeof TRUE | typeof FALSE | boolean;

export type TransferSourceResponse = {
  aggregatorLink?: AggregatorLinkResponse;
  planSponsor: string | null;
  terminatedYear: string | null;
  startYear: string | null;
  salaryRange: SalaryRange | null;
  providerSkipped: SkippableBoolean;
  statementSkipped: SkippableBoolean;
  aggregatorSkipped: SkippableBoolean;
  providerName?: string | null;
  transferId?: number;
  sourceAccountIs401k: SkippableBoolean | null;
};

// TODO: revisit this logic
export const transformSkippableToBoolean = (input: SkippableBoolean | boolean | null): boolean => {
  if (!input) return false;
  if (input === UNKNOWN) return false;
  if (typeof input === "boolean") return input;
  if (input === "TRUE") return true;
  return false;
};

export default class TransferSource {
  transferId = -1;
  sourceAccountId = -1;
  planSponsor = "";
  terminatedYear = "";
  startYear = "";
  salaryRange = "";
  openLoan: typeof UNKNOWN | boolean | null = UNKNOWN;
  providerSkipped: typeof UNKNOWN | boolean | null = UNKNOWN;
  providerName = "";
  statementSkipped: typeof UNKNOWN | boolean | null = UNKNOWN;
  sourceStatementFileNames: string[] = [];
  aggregatorSkipped: typeof UNKNOWN | boolean | null = UNKNOWN;
  aggregatorPublicToken: null | string = null; // From plaid
  aggregatorLink?: AggregatorLink = undefined;
  sourceAccountIs401k?: typeof UNKNOWN | boolean | null = null;

  constructor(params: Partial<TransferSource> = {}) {
    mapKnownValues(this, params);
  }

  get providerNameDisplay() {
    if (this.aggregatorLink) {
      return this.aggregatorLink.aggregatorProviderName;
    }
    if (this.providerName) return this.providerName;
    if (this.providerSkipped) {
      return "Account provider (pending)";
    }
    return "";
  }

  toJSON() {
    return {
      ...this,
      sourceAccountIs401k:
        this.sourceAccountIs401k === false ? "FALSE" : this.sourceAccountIs401k === true ? "TRUE" : "UNKNOWN",
      planSponsor: this.planSponsor || null,
      aggregatorSkipped: "TRUE",
      terminatedYear: this.terminatedYear || null,
      startYear: this.startYear || null,
      salaryRange: this.salaryRange || null,
      sourceAccountId: undefined,
      yetToOpen: undefined,
      providerSkipped: this.providerName === "" ? "TRUE" : "FALSE",
    } as Record<keyof TransferSource, any>;
  }

  addFileNameToStatements(fileName: string) {
    if (this.sourceStatementFileNames.includes(fileName)) return;
    this.sourceStatementFileNames.push(fileName);
  }

  removeFileNameFromStatements(fileName: string) {
    const indexOfFileNameFound = this.sourceStatementFileNames.indexOf(fileName);
    if (indexOfFileNameFound === -1) return;
    this.sourceStatementFileNames.splice(indexOfFileNameFound, 1);
  }

  static fromApi(response: TransferSourceResponse) {
    const newResponse = {
      ...response,
      aggregatorLink: response.aggregatorLink ? new AggregatorLink(response.aggregatorLink) : undefined,
      planSponsor: response.planSponsor || undefined,
      terminatedYear: response.terminatedYear || undefined,
      startYear: response.startYear || undefined,
      salaryRange: response.salaryRange || undefined,
      providerName: response.providerName || undefined,
      providerSkipped:
        response.providerSkipped === "UNKNOWN"
          ? (UNKNOWN as typeof UNKNOWN)
          : response.providerSkipped === "TRUE"
          ? true
          : response.providerSkipped === "FALSE"
          ? false
          : response.providerSkipped,
      statementSkipped:
        response.statementSkipped === "UNKNOWN"
          ? (UNKNOWN as typeof UNKNOWN)
          : response.statementSkipped === "TRUE"
          ? true
          : response.statementSkipped === "FALSE"
          ? false
          : response.statementSkipped,
      aggregatorSkipped:
        response.aggregatorSkipped === "UNKNOWN"
          ? (UNKNOWN as typeof UNKNOWN)
          : response.aggregatorSkipped === "TRUE"
          ? true
          : response.aggregatorSkipped === "FALSE"
          ? false
          : response.aggregatorSkipped,
      sourceAccountIs401k:
        response.sourceAccountIs401k === "UNKNOWN"
          ? (UNKNOWN as typeof UNKNOWN)
          : response.sourceAccountIs401k === "TRUE"
          ? true
          : response.sourceAccountIs401k === "FALSE"
          ? false
          : response.sourceAccountIs401k,
    };
    return new TransferSource(newResponse);
  }
}
